import { useLoginProvider } from '@account-service/core'
import { PUBLIC_AUTH_SERVER_URL } from '@account-service/core/envs'
import type { ErrorResponse } from 'apis'
import { connectSocialAPI, disconnectSocialAPI } from 'apis'
import { useCallback, useEffect, useState } from 'react'

import { EventName, sendEvent } from '~/apis/tracking'
import { ERRORS } from '~/constants'
import { useSentry } from '~/core/sentry'
import { useAuthContext } from '~/hooks/useAuthContext'
import { useWrappedToast } from '~/hooks/useWrappedToast'

import type { TConnectProps } from '../Connect'

const useHooks = (props: TConnectProps) => {
  const { apple } = useLoginProvider()
  const { getProfile } = useAuthContext()
  const { showError, showSuccessMessage } = useWrappedToast()
  const { captureException } = useSentry()

  const [loading, setLoading] = useState(false)

  const handleConnect = useCallback(async () => {
    setLoading(true)
    try {
      const [token, appleError] = await apple.signIn({
        uxMode: 'popup',
        redirectUrl: `${PUBLIC_AUTH_SERVER_URL}v2/public/auth/social/apple/redirect`,
      })

      if (!token || appleError) {
        showError(ERRORS.ERR_LOGIN_SDK)
        return
      }

      if (!props.connected) {
        await connectSocialAPI({
          service: 'apple',
          token,
        })
      } else {
        await disconnectSocialAPI({
          service: 'apple',
          token,
        })
      }

      await getProfile()
      showSuccessMessage(
        `${!props.connected ? 'Link' : 'Unlink'} apple successfully`,
      )

      const eventTracking = props.connected
        ? EventName.DISCONNECT_APPLE
        : EventName.CONNECT_APPLE

      sendEvent({
        type: 'track',
        data: {
          event: eventTracking,
          action: eventTracking,
          ref: eventTracking,
        },
      })
    } catch (error) {
      apple.signOut()
      const { error_status } = error as ErrorResponse

      if (
        [
          'ERR_APPLE_OAUTH_TOKEN_INVALID',
          'ERR_LINK_WITH_EXIST_PROFILE',
          'ERR_ACCOUNT_CONNECTED_WITH_OTHER_EMAIL',
          'ERR_OAUTH_TOKEN_INVALID',
        ].includes(error_status)
      ) {
        showError(ERRORS[error_status])
        return
      }

      captureException(error)
    } finally {
      setLoading(false)
    }
  }, [apple, props.connected])

  useEffect(() => {
    apple.loadSDK()

    return () => apple.removeSDK()
  }, [apple])

  return { loading, handleConnect }
}

export default useHooks
